import React, { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext({
  theme: "system",           // user's chosen preference: "light" | "dark" | "system"
  resolvedTheme: "dark",     // actual palette applied to the DOM
  systemTheme: "dark",       // OS-level preference
  toggle: () => {},
  setTheme: () => {},
});

const KEY = "moxyplay:theme";
const VALID = ["light", "dark", "system"];

const readSystem = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
};

/**
 * ThemeProvider — three-state theme (light / dark / system) with persistence.
 * - `theme` is the user's stored preference and can be "system".
 * - `resolvedTheme` is what actually gets applied to `<html data-theme="…">`.
 * - When theme === "system", we subscribe to the OS `prefers-color-scheme`
 *   media query and update `resolvedTheme` reactively.
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem(KEY);
      return VALID.includes(stored) ? stored : "system";
    } catch {
      return "system";
    }
  });
  const [systemTheme, setSystemTheme] = useState(() => readSystem());

  // Watch OS preference changes
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = (e) => setSystemTheme(e.matches ? "light" : "dark");
    // Older Safari uses addListener
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  const resolvedTheme = theme === "system" ? systemTheme : theme;

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolvedTheme);
    try { localStorage.setItem(KEY, theme); } catch { /* noop */ }
  }, [theme, resolvedTheme]);

  const setTheme = (t) => setThemeState(VALID.includes(t) ? t : "system");

  // Cycle: system → light → dark → system
  const toggle = () =>
    setThemeState((t) => (t === "system" ? "light" : t === "light" ? "dark" : "system"));

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, systemTheme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
